import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';

// Components
import HouseholdComparisonsTable from './tabs/HouseholdComparisons';
import { ControlLinkItem, Tabs } from '@/views/components/navigation';
import HouseholdPortfolios from '@/views/containers/households/household-details/tabs/HouseholdPortfolios';

// Types
import Locale from '@/types/locale';
import { GenericHouseHold } from '@/types/households';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { NAV_PATHS } from '@/utils/constants';
import { resetCurrentContact } from '@/redux/modules/common/actionCreators';

type StateProps = {
  currentHousehold?: GenericHouseHold;
  locale: Locale;
};

type DispatchProps = {
  resetCurrentContact: () => void;
};

type Props = StateProps & DispatchProps;

enum HouseholdDetailsTab {
  Portfolios = 'Portfolios',
  Comparisons = 'Comparisons',
}
enum HouseholdDetailsPaths {
  Portfolios = 'portfolios',
  Comparisons = 'comparisons',
}

const HouseholdDetailsTabs = (props: Props) => {
  const { currentHousehold, resetCurrentContact } = props;

  const DEFAULT_TAB = HouseholdDetailsTab.Portfolios;
  const i18nBasePath = 'Households.Tabs';
  const history = useHistory();

  const [currentTab, setCurrentTab] = useState<string>(DEFAULT_TAB);

  useEffect(() => {
    if (!currentHousehold) {
      return;
    }
    setInitialTab();
    resetCurrentContact();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHousehold]);

  const getSelectedTabFromUrl = () => {
    const selectedTab = getTabs().find(tab => history.location.pathname === tab.route);
    return selectedTab ? selectedTab.value : null;
  };

  const getHouseholdPathUrl = () => {
    if (!currentHousehold) {
      return null;
    }
    return NAV_PATHS.HOUSEHOLD_DETAILS.replace(':householdId', currentHousehold.id);
  };

  const setInitialTab = () => {
    const selectedTab = getSelectedTabFromUrl();
    if (!selectedTab) {
      const path = getHouseholdPathUrl();
      const redirect = `${path}/${HouseholdDetailsPaths[currentTab as keyof typeof HouseholdDetailsTab]}`;
      history.replace(redirect);
      setCurrentTab(currentTab);
    } else {
      setCurrentTab(selectedTab);
    }
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case HouseholdDetailsTab.Comparisons: {
        return <HouseholdComparisonsTable />;
      }
      case HouseholdDetailsTab.Portfolios: {
        return <HouseholdPortfolios />;
      }
    }
  };

  const onTabChange = (tab: HouseholdDetailsTab) => {
    setCurrentTab(tab);
  };

  const getTabs = () => {
    const path = getHouseholdPathUrl();

    return [
      {
        value: HouseholdDetailsTab.Portfolios,
        route: `${path}/${HouseholdDetailsPaths.Portfolios}`,
        i18nPath: formatMessage(`${i18nBasePath}.Portfolios`),
      },
      {
        value: HouseholdDetailsTab.Comparisons,
        route: `${path}/${HouseholdDetailsPaths.Comparisons}`,
        i18nPath: formatMessage(`${i18nBasePath}.Comparisons`),
      },
    ];
  };

  return (
    <div className="household-details-tabs-sections">
      <div className="household-details-tabs-header" data-testid="household-details-tabs">
        <Tabs className="tabs-v2" onChange={onTabChange} selected={currentTab}>
          {getTabs().map(option => (
            <ControlLinkItem ariaLabel={option.i18nPath} key={option.value} route={option.route || '#'} value={option.value}>
              <FormattedMessage id={option.i18nPath} />
            </ControlLinkItem>
          ))}
        </Tabs>
      </div>
      <div className="household-details-tabs-body">{renderTabContent()}</div>
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  currentHousehold: state.households.currentHousehold,
  locale: state.intl.locale,
});

export default connect(mapStateToProps, { resetCurrentContact })(HouseholdDetailsTabs);
